/* eslint-disable complexity */
import React from 'react';
import { Message, OnSubmit } from 'Assistant/Chatbot/types';
import { useTranslation } from 'react-i18next';
import { format, isToday } from 'date-fns';
import { ChatbotMessage } from '../ChatbotMessage';
import { Error } from '../Error';
import { Loading } from '../Loading';
import {
  ChatbotSessionDate,
  ChatbotSessionMessagesWrapper,
  ChatbotSessionTitleBreak,
  ChatbotSessionTitleContainer,
  ChatbotSessionWrapper,
} from './styles';

interface ChatbotSessionProps {
  isCurrentOldestLoadedSession?: boolean;
  date: Date;
  messages: Message[];
  conversationId?: string;
  hasInitialMessage?: boolean;
  loading: boolean;
  error?: boolean;
  onSubmit: OnSubmit;
  onReSubmit(): Promise<void>;
}

/**
 * Component representing a chatbot session. A session is a collection of messages
 * that are grouped by date.
 *
 * @param conversationId - id of the conversation
 * @param hasInitialMessage - boolean indicating if the session has an initial message
 * @param isCurrentOldestLoadedSession - boolean indicating if the session is the oldest loaded session
 * @param date - date of the session
 * @param onSubmit - callback function to handle submission of form
 * @param onReSubmit - callback function to handle resubmission of form
 * @param messages - array of messages to display in session
 * @param loading - boolean indicating if the session is loading
 * @param error - boolean indicating if the session has an error
 */
export const ChatbotSession = ({
  date,
  isCurrentOldestLoadedSession,
  onReSubmit,
  messages,
  conversationId,
  loading,
  error,
}: ChatbotSessionProps) => {
  const { t: translate } = useTranslation('chatbot');

  const hasMessages = messages.length > 0;

  // Render the wrapper when there are messages, or when this (possibly empty)
  // session is awaiting a response or has failed — e.g. the follow-up greeting,
  // whose command message is not added to history, so the loading and error
  // states would otherwise have nowhere to render.
  return hasMessages || loading || error ? (
    <ChatbotSessionWrapper>
      {!isCurrentOldestLoadedSession && (
        <ChatbotSessionTitleContainer>
          <ChatbotSessionTitleBreak>
            <ChatbotSessionDate>
              {isToday(date)
                ? translate('conversation.conversation_today_session_header')
                : format(date, 'MMMM d, yyyy')}
            </ChatbotSessionDate>
          </ChatbotSessionTitleBreak>
        </ChatbotSessionTitleContainer>
      )}

      <ChatbotSessionMessagesWrapper>
        {messages.map((message, index) => (
          <ChatbotMessage
            message={message}
            conversationId={conversationId}
            key={message.id || index}
          />
        ))}

        {
          /** Loading state for assistant messages */
          loading ? <Loading /> : null
        }

        {
          /** Error state for chat message mutation allows
           * re submit of previous messages.
           */
          error ? (
            <Error
              title={translate(
                'conversation.conversation_errors.submit_message_error.title',
              )}
              body={translate(
                'conversation.conversation_errors.submit_message_error.body',
              )}
              onTryAgainPress={onReSubmit}
            />
          ) : null
        }
      </ChatbotSessionMessagesWrapper>
    </ChatbotSessionWrapper>
  ) : null;
};
